import * as ROUTES from 'Routes'
import { ROOT_URL } from 'gatsby-env-variables'
import analytics from '@mobi/libraries/analytics'
import { navigate } from 'gatsby'
import classNames from 'classnames'
import Icon from 'components/Icon'
import Text from 'components/Text'
import ImgStoryblok from 'components/ImgStoryblok'
import Button from 'components/Button'
import router from 'utils/router'
import { ThemeContext } from 'modules/Theme'
import './LandingFooter.scss'
import { useContext } from 'react'

const LandingFooter = ({ blok, location }) => {
  const { theme } = useContext(ThemeContext)

  const isRecoveryOrigin = theme === 'recovery'

  const handleGetYear = new Date().getFullYear()

  const handleTracking = (ev) => {
    const elementClickedPayload = {
      location: ev.currentTarget.dataset.makeLocation,
      position: ev.currentTarget.dataset.makePosition,
      elementType: ev.currentTarget.dataset.makeElement,
      text: ev.currentTarget.textContent || ev.currentTarget.ariaLabel,
      htmlId: ev.currentTarget.dataset.unique,
      actionOutcome: ev.currentTarget.dataset.makeActionoutcome,
      outboundUrl: ev.currentTarget.dataset.makeOutboundurl,
    }

    analytics.track('elementClicked', elementClickedPayload)
  }

  const renderLinks = () => {
    return blok.links.map((item, index) => (
      <a
        className={`label-text label-text__bold ${
          index === 0 ? 'label-text__bold-first' : ''
        }`}
        key={index}
        href={item.url}
        target="_blank"
        rel="noreferrer"
        data-make-location="footer"
        data-make-position="footer-link"
        data-make-actionoutcome="redirect"
        data-make-element="link"
        data-make-outboundurl={item.url}
        onClick={handleTracking}
      >
        {item.text}
      </a>
    ))
  }

  const renderIcons = () => {
    return blok.icons.map((item, index) => (
      <a
        href={item.url}
        aria-label={item.icon_name}
        key={index}
        target="_blank"
        rel="noreferrer"
        data-make-location="footer"
        data-make-position="footer-social-media"
        data-make-actionoutcome="redirect"
        data-make-element="link"
        data-make-outboundurl={item.url}
        onClick={handleTracking}
      >
        <div className="item-icon">
          <Icon iconId={`iconLP${item.icon_name}`} />
        </div>
      </a>
    ))
  }

  const renderItauContainer = () => {
    const splitPhrase = blok?.an_itau_company?.split(' ')
    const firstWord = splitPhrase?.[0]
    const removeFirstWord = splitPhrase?.slice(1).join(' ')

    return (
      <div className="container-itau">
        <Text
          p
          size={1}
          className="label-text label-text__white itau-company"
          aria-label={`${blok?.an_itau_company} Itaú`}
        >
          <span aria-hidden="true">
            <b>{firstWord}</b>
            {removeFirstWord}
          </span>
          <ImgStoryblok
            src={blok.itau_logo.filename}
            width={40}
            height={40}
            alt="Logo Itaú"
            loading="lazy"
            className="itau-company__img"
            aria-hidden="true"
          />
        </Text>
        <Text p size={1} className="label-text label-text__white itau-text">
          © {handleGetYear} Itaú Unibanco Holding S.A.
        </Text>
        <Text p size={1} className="label-text label-text__white itau-text">
          {blok.itau_cnpj}
        </Text>
        <Text p size={1} className="label-text label-text__white">
          {blok.itau_address}
        </Text>
      </div>
    )
  }

  const footerClassnames = classNames('landing-footer-container', {
    'landing-footer-container__generalTheme': !isRecoveryOrigin,
  })

  return (
    <div className={footerClassnames}>
      <div className="landing-footer-content">
        <div className="footer-left-part">
          <div className="logo">
            {!['recovery', 'itaucard'].includes(theme) && (
              <>
                <ImgStoryblok
                  src={blok?.logo_origin?.filename}
                  alt={blok?.logo_origin?.alt || 'Logo'}
                  className="logo-originImage"
                  loading="lazy"
                  dataTest="footer-logo"
                />
                <hr className="logo-partition" />
              </>
            )}
            <ImgStoryblok
              src={blok?.logo_recovery?.filename}
              width={156}
              height={40}
              alt={blok?.logo_recovery?.alt || 'Logo Recovery'}
              loading="lazy"
              dataTest="footer-logo"
            />
          </div>
          <Text h2 size={3} className="footer-main-text">
            {blok.title_text}
          </Text>
          <Button
            className="left-part-button"
            onClick={() => {
              navigate(ROUTES.register.path, {
                state: {
                  source_url: location.href,
                  mp_uuid: router.getQueryString('mp_uuid'),
                },
              })
            }}
            data-unique="33e4d860-1f1b-47b8-a65e-d30f08c67647"
            data-test="footer-consult-debt-button"
            data-make-location="footer"
            data-make-position="footer-cta"
            data-make-actionoutcome="check_cpf"
            data-make-element="button"
            data-make-outboundurl={`${ROOT_URL}${ROUTES.register.path}`}
          >
            {blok.button_text}
          </Button>
        </div>

        <div className="footer-right-part">
          {renderLinks()}

          <Text
            p
            size={9}
            className="label-text margin-links label-text__white"
          >
            {blok.cnpj_text}
          </Text>

          <Text
            p
            size={9}
            className="label-text margin-icons label-text__white"
          >
            {blok.address_text}
          </Text>

          <div className="icon-container" data-test="footer-icon-container">
            {renderIcons()}
          </div>

          <Text p size={1} className="label-text label-text__white">
            © Copyright {handleGetYear} Grupo Recovery, Todos os direitos
            reservados
          </Text>
          {['recovery', 'itaucard'].includes(theme) && renderItauContainer()}
        </div>
      </div>
    </div>
  )
}

export default LandingFooter
